import React, { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import FilterDropdown, { DARK_THEME, FilterDropdownTheme } from 'components/search-bar/advanced-search/filter-dropdown';
import ButtonGroup from 'components/control/button-group';
import Checkbox from 'components/control/checkbox';
import { searchFilterIds } from 'constants/test-constants';
import { getLocalizedBedroomBathroomParkingOptions } from 'utils/localized-select-options';
import { parameterContainsIsMore, toggleContainsMore } from 'utils/listing-query-helper';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';

import type { Bedroom } from 'contexts/preferences/listing-params/types';

//#region Constants
const BedroomsRegex = new RegExp(/^[0-6]\+?$/);
//#endregion

//#region Types
export type Bedrooms = `${Bedroom}` | `${Bedroom}+`;

export interface BedroomsFilterDropdownProps {
  bedrooms: Bedrooms;
  theme?: FilterDropdownTheme;
  onValueChange(value: string, checkedUseExact?: boolean): void;
}
//#endregion

const BedroomsFilterDropdown = ({ bedrooms, onValueChange, theme = DARK_THEME }: BedroomsFilterDropdownProps) => {
  const { t } = useTranslation(Namespaces.SEARCH);
  
  const sanitizedBedrooms = useMemo(() => {
    return BedroomsRegex.test(bedrooms) ? bedrooms : '0+';
  }, [bedrooms]);

  const [isExactBedroomNumber, setIsExactBedroomNumber] = useState(!parameterContainsIsMore(sanitizedBedrooms));
  const bedroomNumberOptions = getLocalizedBedroomBathroomParkingOptions(t, isExactBedroomNumber);

  const bedroomsFilterLabel = useMemo(() => {
    return `${sanitizedBedrooms} ${t(LOCALE_SEARCH_PATHS.FILTER_LABELS_BEDS, { defaultValue: 'Beds' })}`;
  }, [sanitizedBedrooms, t]);

  return (
    <FilterDropdown
      theme={theme}
      label={bedroomsFilterLabel}
      testId={searchFilterIds.bedsDropdownMap}
    >
      <>
        <ButtonGroup
          options={bedroomNumberOptions}
          value={sanitizedBedrooms}
          onValueChange={onValueChange}
          size={'small'}
        />
        <Checkbox
          label={t(LOCALE_SEARCH_PATHS.FILTER_LABELS_USE_EXACT_NUMBER, { defaultValue: 'Use exact number' })}
          name="beds"
          value={isExactBedroomNumber}
          onValueChange={checked => {
            const toggledCheckbox = isExactBedroomNumber != checked;
            if (sanitizedBedrooms !== '0+') {
              onValueChange(toggleContainsMore(bedrooms), toggledCheckbox);
            }
            setIsExactBedroomNumber(checked);
          }}
          testId={searchFilterIds.bedsCheckboxSavedSearches}
        />
      </>
    </FilterDropdown>
  );
};

export default BedroomsFilterDropdown;